/** @format */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Pagination from "../../elements/Pagination";
import { debounce } from 'lodash';

export default function Bookings() {
  const [booking, setBooking] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(() => Number(sessionStorage.getItem('bookings_currentPage')) || 0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(() => sessionStorage.getItem('bookings_fromDate') || '');
  const [toDate, setToDate] = useState(() => sessionStorage.getItem('bookings_toDate') || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [limit, setLimit] = useState(() => Number(sessionStorage.getItem('bookings_limit')) || 10);

  const getBookingList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `booking?page=${currentPage + 1}&limit=${limit}`;
      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const response = await ApiService.get(url);
      setLoading(false);
      if (response) {
        // Convert updatedAt to IST before setting booking data
        const bookingsWithIST = response.result.data.map((dp) => ({
          ...dp,
          updatedAt: convertToIST(dp.updatedAt),
          createdAt: convertToIST(dp.createdAt),
        }));
        setBooking(bookingsWithIST);
        setPageCount(response.result.totalPages);
      } else {
        setBooking([]);
        setPageCount(0);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data || "Failed to fetch booking data");
    }
  }, [currentPage, fromDate, toDate, statusFilter, limit]);

  const searchBookings = useCallback(async (term) => {
    setLoading(true);
    try {
      const response = await ApiService.get(
        `booking/search?searchTerm=${term}&page=${currentPage + 1}&limit=${limit}`
      );
      setLoading(false);
      if (response) {
        const bookingsWithIST = response.result.data.map((dp) => ({
          ...dp,
          updatedAt: convertToIST(dp.updatedAt),
          createdAt: convertToIST(dp.createdAt),
        }));
        setBooking(bookingsWithIST);
        setPageCount(response.result.totalPages);
      } else {
        setBooking([]);
        setPageCount(0);
      }
    } catch (err) {
      setLoading(false);
      toast.error(err?.response?.data || "Failed to search bookings");
    }
  }, [currentPage, limit]);

  // Debounced search function
  const debouncedSearch = useMemo(() => debounce((value) => {
    searchBookings(value);
  }, 500), [searchBookings]);

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    } else {
      getBookingList();
    }
  }, [currentPage, searchTerm, debouncedSearch, getBookingList]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    setFromDate(val);
    sessionStorage.setItem('bookings_fromDate', val);
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    setToDate(val);
    sessionStorage.setItem('bookings_toDate', val);
  };

  const handleDateFilter = () => {
    setCurrentPage(0);
    sessionStorage.setItem('bookings_currentPage', 0);
    getBookingList();
  };

  const clearDateFilter = () => {
    setFromDate('');
    setToDate('');
    setCurrentPage(0);
    sessionStorage.removeItem('bookings_fromDate');
    sessionStorage.removeItem('bookings_toDate');
    sessionStorage.setItem('bookings_currentPage', 0);
  };

  const convertToIST = (utcDate) => {
    const date = new Date(utcDate);
    return date.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  const handleLimitChange = (e) => {
    const val = Number(e.target.value);
    setLimit(val);
    sessionStorage.setItem('bookings_limit', val);
    setCurrentPage(0);
    sessionStorage.setItem('bookings_currentPage', 0);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(prev => prev === status ? '' : status);
    setCurrentPage(0);
    sessionStorage.setItem('bookings_currentPage', 0);
  };

  const handlePageClick = (selectedPage) => {
    const clickedPage = selectedPage.selected;
    if (clickedPage !== currentPage) {
      setCurrentPage(clickedPage);
      sessionStorage.setItem('bookings_currentPage', clickedPage);
    }
  };
  useEffect(() => {
    const interval = setInterval(() => {
      window.location.reload(); // Refresh the page
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []);

  return (
    <div className="row">
      <div className="col-xl-12">
        {loading && <Loader />}
        <div className="card dz-card" id="bootstrap-table2">
          <div className="card-header flex-wrap d-flex justify-content-between border-0">
            <div>
              <h4 className="card-title">Booking List</h4>
            </div>
            <div className="d-flex align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 fw-semibold" style={{ whiteSpace: 'nowrap' }}>Puja Date:</label>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: '160px' }}
                  value={fromDate}
                  onChange={handleFromDateChange}
                  placeholder="From"
                />
                <span className="mb-0">to</span>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: '160px' }}
                  value={toDate}
                  onChange={handleToDateChange}
                  placeholder="To"
                />
                <button className="btn btn-primary btn-sm" onClick={handleDateFilter}>Apply</button>
                {(fromDate || toDate) && (
                  <button className="btn btn-outline-secondary btn-sm" onClick={clearDateFilter}>Clear</button>
                )}
              </div>
              <div className="input-group search-area" style={{ width: '250px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
                <span className="input-group-text">
                  <i className="flaticon-381-search-2"></i>
                </span>
              </div>
              <Link to="/add-booking" className="btn btn-primary ms-2">
                Add Booking
              </Link>
            </div>
          </div>
          <div className="px-4 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold me-1" style={{ fontSize: '0.85rem' }}>Filter:</span>
              {[
                { label: 'Acharya Assigned', value: 'acharya_assigned' },
                { label: 'Puja Completed',   value: 'puja_completed' },
                { label: 'Cancelled',        value: 'cancelled' },
                { label: 'Refund',           value: 'refund' },
              ].map(({ label, value }) => (
                <button
                  key={value}
                  className={`btn btn-sm ${statusFilter === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleStatusFilter(value)}
                  style={{ borderRadius: '20px' }}
                >
                  {label}
                </button>
              ))}
              {statusFilter && (
                <button
                  className="btn btn-sm btn-link text-danger p-0 ms-1"
                  onClick={() => handleStatusFilter(statusFilter)}
                >
                  Clear
                </button>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 fw-semibold" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Rows per page:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: '80px' }}
                value={limit}
                onChange={handleLimitChange}
              >
                {[10, 25, 50, 100].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-responsive-md">
                <thead>
                  <tr>
                    <th>
                      <strong>Sr No.</strong>
                    </th>
                    <th>
                      <strong>Booking ID</strong>
                    </th>
                    <th>
                      <strong>Service</strong>
                    </th>
                    <th>
                      <strong>Variant</strong>
                    </th>
                    <th>
                      <strong>BE email</strong>
                    </th>
                    <th>
                      <strong>User Contact</strong>
                    </th>
                    <th>
                      <strong>Customer Name</strong>
                    </th>
                    <th>
                      <strong>Label</strong>
                    </th>
                    <th>
                      <strong>Customer Call Status</strong>
                    </th>
                    <th>
                      <strong>Agent Call Status</strong>
                    </th>
                    <th>
                      <strong>Booking Status</strong>
                    </th>
                    <th>
                      <strong>Puja Date</strong>
                    </th>
                    <th>
                      <strong>Created At</strong>
                    </th>
                    <th>
                      <strong>Action</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {booking && booking.length > 0 ? (
                    booking.map((dp, key) => (
                      <tr key={key}>
                        <td>
                          <strong>{currentPage * limit + key + 1}</strong>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.bookingId}</span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.service?.title || ""}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.isCustomPuja ? (
                              "Custom Package"
                            ) : (!dp.variantId && dp.bookingStatus === 'pending') ? (
                              "-"
                            ) : (!dp.variant || !dp.variantId) ? (
                              "Basic"
                            ) : (
                              dp.variant.variantType || "Basic"
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.be?.email || ""}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.user?.phone || ""}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.user?.name || ""}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.modelType}</span>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.customerCallStatus}</span>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.agentCallStatus}</span>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.bookingStatus === 'pending' ? 'puja booked but not confirmed' : dp.bookingStatus}</span>
                        </td>
                        <td>
                          <span className="w-space-no">
                            {dp.pujaDateTime ? new Date(dp.pujaDateTime).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-"}
                          </span>
                        </td>
                        <td>
                          <span className="w-space-no">{dp.createdAt}</span>
                        </td>
                        <td>
                          <Link
                            to={`/edit-booking/${dp.bookingId}`}
                            className="btn btn-primary shadow btn-xs sharp me-1"
                          >
                            <i className="fa fa-pencil" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="13" className="text-center">
                        No bookings found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination pageCount={pageCount} handlePageClick={handlePageClick} currentPage={currentPage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
